/**
 * @file ChefTopBar.tsx
 * @description Header console bar with venue info, station filters, and All-Day drawer trigger using TableGreet branding.
 */

import React from 'react';
import { Layers, BarChart3, Maximize2, LogOut, SlidersHorizontal, Flame } from 'lucide-react';
import type { KitchenLoadStatus } from '../../features/kitchenops/types/kitchenOps.types';
import logo from '../../assets/logo.svg';

interface ChefTopBarProps {
  venueName: string;
  onOpenAllDay: () => void;
  onOpenStats: () => void;
  onOpenKitchenOps: () => void;
  onLogout: () => void;
  activeOrderCount: number;
  kitchenLoadStatus?: KitchenLoadStatus;
  hasWhiteboardNote?: boolean;
}

const LOAD_STATUS_COLORS: Record<KitchenLoadStatus, string> = {
  NORMAL: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  BUSY: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  RUSH: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  OVERLOAD: 'bg-rose-500/25 text-rose-400 border-rose-500/50 animate-pulse',
};

export const ChefTopBar: React.FC<ChefTopBarProps> = ({
  venueName,
  onOpenAllDay,
  onOpenStats,
  onOpenKitchenOps,
  onLogout,
  activeOrderCount,
  kitchenLoadStatus = 'NORMAL',
  hasWhiteboardNote = false,
}) => {
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  return (
    <header className="bg-[#121212] border-b border-white/10 px-4 py-3 shrink-0">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Real TableGreet Logo & Venue Name */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} className="w-9 h-9 object-contain shrink-0" alt="TableGreet™" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-black text-base tracking-tight bg-gradient-to-r from-[#ff4f00] to-orange-400 bg-clip-text text-transparent">
                  TableGreet<sup className="text-[0.55em] font-bold align-super">™</sup>
                </span>
                <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-[#ff4f00]/15 text-[#ff4f00] border border-[#ff4f00]/30 tracking-wider">
                  Chef
                </span>
                {/* Real-time glanceable Kitchen Load Status Badge */}
                <button
                  type="button"
                  onClick={onOpenKitchenOps}
                  className={`hidden sm:inline-flex items-center gap-1 text-[9px] font-black uppercase px-2 py-0.5 rounded-full border transition-all cursor-pointer ${LOAD_STATUS_COLORS[kitchenLoadStatus]}`}
                  title="Kitchen Load Level (Click to change)"
                >
                  <Flame className="w-2.5 h-2.5" />
                  <span>{kitchenLoadStatus}</span>
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-400 font-mono mt-0.5 min-w-0">
                <span className="text-zinc-200 font-semibold uppercase truncate max-w-[110px] sm:max-w-[200px]">{venueName || 'Kitchen Terminal'}</span>
                <span>•</span>
                <span className="text-[#ff4f00] font-bold shrink-0">
                  {activeOrderCount} {activeOrderCount === 1 ? 'TICKET' : 'TICKETS'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick mobile controls */}
          <div className="flex md:hidden items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={onOpenKitchenOps}
              className="min-w-[42px] min-h-[42px] relative flex items-center justify-center rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs font-bold active:scale-95 transition-all"
              title="Kitchen Ops & 86"
              aria-label="Kitchen Ops & 86"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {hasWhiteboardNote && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-400 border border-black" />
              )}
            </button>
            <button
              type="button"
              onClick={onOpenAllDay}
              className="min-w-[42px] min-h-[42px] flex items-center justify-center rounded-xl bg-[#ff4f00]/10 border border-[#ff4f00]/25 text-[#ff4f00] text-xs font-bold active:scale-95 transition-all"
              title="All Day Production"
              aria-label="All Day Production"
            >
              <Layers className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onOpenStats}
              className="min-w-[42px] min-h-[42px] flex items-center justify-center rounded-xl bg-[#1e1e1e] border border-white/10 text-zinc-300 text-xs font-bold active:scale-95 transition-all"
              title="Production KPIs"
              aria-label="Production KPIs"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="min-w-[42px] min-h-[42px] flex items-center justify-center rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-bold active:scale-95 transition-all"
              title="Log Out"
              aria-label="Log Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>


        {/* Action Controls */}
        <div className="hidden md:flex items-center gap-2">
          {/* Kitchen Ops & 86 Drawer trigger */}
          <button
            type="button"
            onClick={onOpenKitchenOps}
            className="relative flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-900 text-zinc-200 border border-zinc-800 hover:text-white hover:border-zinc-700 text-xs font-bold transition-colors cursor-pointer"
          >
            <SlidersHorizontal className="w-4 h-4 text-amber-400" />
            <span>Ops & 86</span>
            {hasWhiteboardNote && (
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" title="Daily whiteboard active" />
            )}
          </button>

          <button
            type="button"
            onClick={onOpenAllDay}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#ff4f00]/10 text-[#ff4f00] border border-[#ff4f00]/25 hover:bg-[#ff4f00]/20 text-xs font-bold transition-colors cursor-pointer"
          >
            <Layers className="w-4 h-4" />
            <span>All Day</span>
          </button>

          <button
            type="button"
            onClick={onOpenStats}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#181818] text-zinc-300 border border-white/10 hover:text-white hover:border-white/20 text-xs font-bold transition-colors cursor-pointer"
          >
            <BarChart3 className="w-4 h-4" />
            <span>KPIs</span>
          </button>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-2 rounded-xl bg-[#181818] text-zinc-400 border border-white/10 hover:text-white transition-colors cursor-pointer"
            title="Toggle Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={onLogout}
            className="p-2 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 transition-colors cursor-pointer"
            title="Log Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
